import { NotificationStatusEnum } from '@elearning-platform/api-types/enums';
import { NotificationInterface, NotificationsResponseInterface } from '@elearning-platform/api-types/interfaces';
import { Color, IconName, useNotifications, UseNotifications, UseState } from '@elearning-platform/ui';
import { InfiniteData, QueryClient, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { deleteNotification, markNotificationAsRead } from '../../../api/requests';
import { QueryKey } from '../../../enums';
import { UseNotificationsActions } from '../interfaces';

export const useNotificationsActions: () => UseNotificationsActions = (): UseNotificationsActions => {
  const { showPopup, hidePopup, addToast }: UseNotifications = useNotifications();
  const { t }: TransProps<never> = useTranslation();
  const queryClient: QueryClient = useQueryClient();
  const [removingIds, setRemovingIds]: UseState<Set<number>> = useState<Set<number>>(new Set());

  const { mutate: removeNotification }: UseMutationResult<void, Error, number> = useMutation({
    mutationFn: (id: number): Promise<void> => deleteNotification(id),
    onMutate: (id: number): void => {
      setRemovingIds((prev: Set<number>): Set<number> => new Set(prev).add(id));
    },
    onSuccess: (_: void, id: number): void => {
      queryClient.setQueryData(
        [QueryKey.Notifications],
        (prev: InfiniteData<NotificationsResponseInterface>): InfiniteData<NotificationsResponseInterface> => ({
          ...prev,
          pages: prev.pages.map((page: NotificationsResponseInterface): NotificationsResponseInterface => ({
            ...page,
            data: page.data.filter((notification: NotificationInterface): boolean => notification.id !== id),
          })),
        })
      );
      addToast({ text: t('competence.app.notifications.notificationDeleted'), icon: IconName.CheckmarkCircle, iconColor: Color.Green300 });
    },
    onError: (error: Error): void => showPopup({
      title: t('competence.app.global.errorOccurred'),
      description: error.message || t('competence.app.global.defaultErrorDescription'),
      acceptButtonData: {
        label: t('competence.app.global.close'),
        onClick: (): void => hidePopup(),
      },
    }),
    onSettled: (_: void, error: Error | null, id: number): void => {
      setRemovingIds((prev: Set<number>): Set<number> => {
        const next: Set<number> = new Set(prev);
        next.delete(id);

        return next;
      });
    },
  });

  const { mutate: readNotification }: UseMutationResult<void, Error, number> = useMutation({
    mutationFn: (id: number): Promise<void> => markNotificationAsRead(id),
    onSuccess: (_: void, id: number): void => {
      queryClient.setQueryData(
        [QueryKey.Notifications],
        (prev: InfiniteData<NotificationsResponseInterface>): InfiniteData<NotificationsResponseInterface> => ({
          ...prev,
          pages: prev.pages.map((page: NotificationsResponseInterface): NotificationsResponseInterface => ({
            ...page,
            data: page.data.map((notification: NotificationInterface): NotificationInterface => notification.id === id
              ? { ...notification, status: { ...notification.status, name: NotificationStatusEnum.READ } }
              : notification
            ),
          })),
        })
      );
    },
    onError: (error: Error): void => showPopup({
      title: t('competence.app.global.errorOccurred'),
      description: error.message || t('competence.app.global.defaultErrorDescription'),
      acceptButtonData: {
        label: t('competence.app.global.close'),
        onClick: (): void => hidePopup(),
      },
    }),
  });

  const isRemoving: (id: number) => boolean = (id: number): boolean => removingIds.has(id);

  return { removeNotification, readNotification, isRemoving };
};
